import React, { useState, useEffect, useRef } from 'react';
import { HiOutlineRefresh } from 'react-icons/hi';
import { getPendingOrders, getOrderById, getPendingOrderById } from '../services/api';
import { refreshAccessToken } from '../services/auth';
import { formatDateTime, getStatusColor, handleApiError, calculateOrderTotal, getDiscountDetails } from '../utils/helpers';
import OrderDetailsView from './OrderDetailsView';
import DateRangePicker from '../components/common/DateRangePicker';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PendingOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [originalOrders, setOriginalOrders] = useState([]); // Store original pending orders
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const { accessToken } = useAuth();
    const [searchQuery, setSearchQuery] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [exportStartDate, setExportStartDate] = useState('');
    const [exportEndDate, setExportEndDate] = useState('');

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);
    const autoRetryRef = useRef(false);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            // Use real API to get pending orders
            const result = await getPendingOrders();

            // Handle paginated response
            let ordersArray;
            if (result.content && result.totalElements) {
                // Paginated response
                ordersArray = result.content;
            } else {
                // Simple array response
                ordersArray = result;
            }

            // Ensure we only work with pending orders
            const pendingOrders = ordersArray.filter(order => order.status === 'PENDING');
            setOrders(pendingOrders);

            // Fetch details for each order to get dynamic totals and discount details
            const detailsPromises = pendingOrders.map(async (order) => {
                try {
                    const details = await getOrderById(order.id);
                    return { ...order, ...details };
                } catch (e) {
                    return order; // fallback if details fail
                }
            });
            let ordersWithTotals = await Promise.all(detailsPromises);
            // Sort by createdAt (newest first)
            ordersWithTotals = ordersWithTotals.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setOrders(ordersWithTotals);
            setOriginalOrders(ordersWithTotals); // Store original orders
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            if (
                !autoRetryRef.current &&
                err &&
                typeof err.message === "string" &&
                err.message.includes("Request failed after repeated attempts")
            ) {
                autoRetryRef.current = true;
                setTimeout(() => {
                    fetchOrders();
                }, 1000);
                return;
            }
            setError('Failed to fetch pending orders');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (orderId) => {
        setDetailsError(null);
        setDetailsLoading(true);
        try {
            const details = await getOrderById(orderId);
            setSelectedOrder(details);
        } catch (err) {
            if (handleApiError(err, navigate)) return;
            setDetailsError('Failed to fetch order details');
            setSelectedOrder(null);
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleBackToList = () => {
        setSelectedOrder(null);
        setDetailsError(null);
    };

    // Utility: check if query is likely an order ID (all digits or starts with 'ORD-')
    const isOrderId = (query) => /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);

    // Synchronous input handler with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear previous timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Cancel previous request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        // Debounce the search
        searchTimeoutRef.current = setTimeout(() => {
            performSearch(value);
        }, 300); // 300ms delay
    };

    // Perform the actual search
    const performSearch = async (query) => {
        console.log('Performing search for:', query);
        console.log('Original orders count:', originalOrders.length);

        // Check if search query is empty or just whitespace
        if (!query || !query.trim()) {
            // No search query - restore original pending orders
            setOrders(originalOrders);
            setSearchLoading(false);
            return;
        }

        // Check if it's a numeric search (all digits or starts with 'ORD-')
        const isNumericSearch = /^\d+$/.test(query) || /^ORD-?\d+$/i.test(query);
        console.log('Is numeric search:', isNumericSearch);

        if (isNumericSearch) {
            // Use backend API for pending orders only
            setSearchLoading(true);
            setError(null);
            try {
                console.log('Calling getPendingOrderById with:', query);
                const order = await getPendingOrderById(query);
                console.log('Search result:', order);
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                if (order && order.id && order.status === 'PENDING') {
                    setOrders([order]);
                } else {
                    setOrders([]);
                }
            } catch (err) {
                // Check if this request was cancelled
                if (abortControllerRef.current?.signal.aborted) {
                    return;
                }
                console.error('Search error:', err);
                setOrders([]);
            }
            setSearchLoading(false);
        } else {
            // Client-side filtering for non-numeric queries
            let filtered = originalOrders; // Use originalOrders instead of orders to ensure we only filter pending orders
            filtered = filtered.filter(order =>
                String(order.id).toLowerCase().includes(query.toLowerCase()) && order.status === 'PENDING'
            );
            console.log('Client-side filtered results:', filtered);
            setOrders(filtered);
            setSearchLoading(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    // Refetch on accessToken change or window focus
    useEffect(() => {
        fetchOrders();
        // Removed window focus refresh
    }, [accessToken]);

    // Don't return early for loading/error - show header and search bar always

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Pending Orders</h1>
                <p className="text-gray-600">Monitor your pending order requests</p>
            </div>
            {!selectedOrder && (
                <div className="mb-4">
                    <input
                        type="text"
                        placeholder="Search by Order ID..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchLoading && (
                        <div className="mt-2 text-sm text-gray-500">Searching...</div>
                    )}
                    <div className="mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <DateRangePicker
                            value={{ startDate: exportStartDate, endDate: exportEndDate }}
                            onApply={(s, e) => {
                                setExportStartDate(s);
                                setExportEndDate(e);
                            }}
                        />
                    </div>
                </div>
            )}
            {selectedOrder ? (
                <div>
                    <button
                        className="mb-4 px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-800"
                        onClick={handleBackToList}
                    >
                        ← Back to List
                    </button>
                    {detailsLoading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : detailsError ? (
                        (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">{detailsError}</div>
                        )
                    ) : (
                        <OrderDetailsView order={selectedOrder} />
                    )}
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        {loading ? (
                            <div className="flex justify-center items-center h-32">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                            </div>
                        ) : error ? (
                            (
                                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-center">{error}</div>
                            )
                        ) : orders.length === 0 ? (
                            <div className="p-8 text-center">
                                <p className="text-gray-500 text-lg">No pending orders found</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Order ID
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Total
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {(() => {
                                        const start = exportStartDate ? new Date(`${exportStartDate}T00:00:00`) : null;
                                        const end = exportEndDate ? new Date(`${exportEndDate}T23:59:59`) : null;
                                        const filteredOrders = orders.filter(order => {
                                            if (order.status !== 'PENDING') return false;
                                            const created = new Date(order.createdAt);
                                            if (start && created < start) return false;
                                            if (end && created > end) return false;
                                            return true;
                                        });
                                        return filteredOrders.map((order) => (
                                            <tr key={order.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {order.id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {(() => {
                                                        const details = order.discountDetails;
                                                        if (details && details.paidAmountAfterDiscount != null) {
                                                            const showBadge = Number(details.discountByAmount) > 0 || Number(details.discountByPercentage) > 0;
                                                            return (
                                                                <div className="flex flex-col">
                                                                    <span className="text-xs text-gray-400 line-through">
                                                                        IQD {Number(details.paidPrice).toLocaleString()}
                                                                    </span>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-bold text-gray-900">
                                                                            IQD {Number(details.paidAmountAfterDiscount).toLocaleString()}
                                                                        </span>
                                                                        {showBadge && (
                                                                            <span className="text-xs font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-100 whitespace-nowrap">
                                                                                -{Number(details.discountByPercentage) > 0 ? `${Number(details.discountByPercentage)}%` : `IQD ${Number(details.discountByAmount).toLocaleString()}`}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                        const fallback = order.totalAmountToBePaid ?? order.totalAmount;
                                                        if (fallback === null || fallback === undefined) {
                                                            return <span className="text-gray-400">N/A</span>;
                                                        }
                                                        return `IQD ${Number(fallback).toLocaleString()}`;
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(order.status)}`}>
                                                        {order.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDateTime(order.createdAt)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button className="text-blue-600 hover:text-blue-900" onClick={() => handleViewDetails(order.id)}>
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingOrders;
